/**
 * The browser's local-first database (docs/local-first-food-data.md, phase 2), opened with raw
 * IndexedDB: not localforage, and not localforage's database.
 *
 * Version 1 creates every store the later phases use, so no later phase has to upgrade the database
 * while another tab or the service worker holds it open. Every store has out-of-line keys:
 *
 * - snapshots: a persisted copy of one budget's data, keyed by persistedStore.recordKey()
 * - ops, op_results: the op queue and its results (phase 3), keyed by client_id
 * - meta: small named values, such as `session`
 *
 * Both this and memoryDb() offer one call, `run(storeNames, mode, work)`: `work(tx)` runs inside one
 * transaction and returns a promise, and `run` resolves with its value once the transaction has
 * committed. A `work` that throws or rejects aborts the transaction, so nothing it wrote lands.
 * Inside `work`, await only the `tx` calls: awaiting anything else lets the transaction commit.
 */

export const DB_NAME = 'budget-local-first';
export const DB_VERSION = 1;
export const STORES = ['snapshots', 'ops', 'op_results', 'meta'];

const requested = (request) => new Promise((resolve, reject) => {
	request.onsuccess = () => resolve(request.result);
	request.onerror = () => reject(request.error);
});

/** The `tx` handed to `work`, over one IndexedDB transaction. */
const idbTransaction = (transaction) => ({
	get: (store, key) => requested(transaction.objectStore(store).get(key)),
	put: (store, key, value) => requested(transaction.objectStore(store).put(value, key)),
	delete: (store, key) => requested(transaction.objectStore(store).delete(key)),
	keys: (store) => requested(transaction.objectStore(store).getAllKeys()),
	entries: (store) => {
		const objectStore = transaction.objectStore(store);
		return Promise.all([requested(objectStore.getAllKeys()), requested(objectStore.getAll())])
			.then(([keys, values]) => keys.map((key, index) => ({ key, value: values[index] })));
	},
	clear: (store) => requested(transaction.objectStore(store).clear()),
});

const wrapIdb = (idb) => {
	let closed = false;
	const close = () => {
		if(!closed) {
			closed = true;
			idb.close();
		}
	};
	// Another tab, or a logout, wants the database deleted or upgraded: let it.
	idb.onversionchange = close;
	return {
		persistent: true,
		get closed() {
			return closed;
		},
		close,
		run(storeNames, mode, work) {
			return new Promise((resolve, reject) => {
				if(closed) {
					reject(new Error('The local database is closed.'));
					return;
				}
				let transaction;
				try {
					transaction = idb.transaction(storeNames, mode);
				} catch(error) {
					reject(error);
					return;
				}
				let result;
				let failure = null;
				transaction.oncomplete = () => (failure ? reject(failure) : resolve(result));
				transaction.onabort = () => reject(failure || transaction.error || new Error('The transaction was aborted.'));
				Promise.resolve()
					.then(() => work(idbTransaction(transaction)))
					.then((value) => {
						result = value;
					}, (error) => {
						failure = error;
						try {
							transaction.abort();
						} catch(abortError) {
							// Already finished; oncomplete or onabort reports it.
						}
					});
			});
		},
	};
};

/** How long opening the database may take before the page gives up on it for this load. */
export const OPEN_TIMEOUT_MS = 5000;

/**
 * Opens the database. Rejects where IndexedDB is missing or refuses (some private windows), or has
 * not answered within `timeoutMs` (some Safari versions never do), and the caller falls back to
 * memoryDb(). A connection that opens after the timeout is closed.
 */
export function openDb({ indexedDB = globalThis.indexedDB, name = DB_NAME, timeoutMs = OPEN_TIMEOUT_MS } = {}) {
	return new Promise((resolve, reject) => {
		if(!indexedDB) {
			reject(new Error('IndexedDB is not available.'));
			return;
		}
		let request;
		try {
			request = indexedDB.open(name, DB_VERSION);
		} catch(error) {
			reject(error);
			return;
		}
		let timedOut = false;
		const timer = setTimeout(() => {
			timedOut = true;
			reject(new Error('The local database did not open in time.'));
		}, timeoutMs);
		request.onupgradeneeded = () => {
			const idb = request.result;
			for(const store of STORES) {
				if(!idb.objectStoreNames.contains(store)) {
					idb.createObjectStore(store);
				}
			}
		};
		request.onsuccess = () => {
			clearTimeout(timer);
			if(timedOut) {
				request.result.close();
				return;
			}
			resolve(wrapIdb(request.result));
		};
		request.onerror = () => {
			clearTimeout(timer);
			reject(request.error);
		};
	});
}

/** openDb(), or memoryDb() when it cannot be opened. Check `persistent` to tell which. */
export function openDbOrMemory(options = {}) {
	return openDb(options).catch((error) => {
		if(options.indexedDB || globalThis.indexedDB) {
			console.error('The local database could not be opened; nothing will survive a reload.', error);
		}
		return memoryDb();
	});
}

/**
 * Deletes the whole database. Resolves 'deleted', or 'blocked' when a connection that ignores
 * `versionchange` keeps it open; the caller then clears the stores instead.
 */
export function deleteDb({ indexedDB = globalThis.indexedDB, name = DB_NAME } = {}) {
	return new Promise((resolve, reject) => {
		if(!indexedDB) {
			resolve('deleted');
			return;
		}
		const request = indexedDB.deleteDatabase(name);
		request.onsuccess = () => resolve('deleted');
		request.onerror = () => reject(request.error);
		request.onblocked = () => resolve('blocked');
	});
}

/** Clears every store, for when the database cannot be deleted. */
export function clearDb(db) {
	return db.run(STORES, 'readwrite', (tx) => Promise.all(STORES.map((store) => tx.clear(store))));
}

const copy = (value) => {
	if(value === undefined) {
		return undefined;
	}
	return typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));
};

const compareKeys = (a, b) => {
	if(typeof a === 'number' && typeof b === 'number') {
		return a - b;
	}
	return String(a) < String(b) ? -1 : (String(a) > String(b) ? 1 : 0);
};

const DELETED = Symbol('deleted');

/**
 * The same interface held in memory: the tests' database, and the fallback where IndexedDB is not
 * available, with `persistent` false. Transactions run one at a time, values are copied in and out
 * as IndexedDB copies them, and a readwrite transaction's writes land only when its work resolves.
 */
export function memoryDb() {
	const data = new Map(STORES.map((store) => [store, new Map()]));
	let queue = Promise.resolve();
	let closed = false;

	const transactionOver = (storeNames) => {
		const writes = new Map();
		const cleared = new Set();
		const check = (store) => {
			if(!storeNames.includes(store)) {
				throw new Error(`${store} is not in this transaction.`);
			}
			if(!writes.has(store)) {
				writes.set(store, new Map());
			}
			return writes.get(store);
		};
		const current = (store) => {
			const pending = check(store);
			const merged = new Map(cleared.has(store) ? [] : data.get(store));
			for(const [key, value] of pending) {
				if(value === DELETED) {
					merged.delete(key);
				} else {
					merged.set(key, value);
				}
			}
			return merged;
		};
		const tx = {
			get: (store, key) => Promise.resolve().then(() => copy(current(store).get(key))),
			put: (store, key, value) => Promise.resolve().then(() => {
				check(store).set(key, copy(value));
			}),
			delete: (store, key) => Promise.resolve().then(() => {
				check(store).set(key, DELETED);
			}),
			keys: (store) => Promise.resolve().then(() => [...current(store).keys()].sort(compareKeys)),
			entries: (store) => Promise.resolve().then(() => {
				const merged = current(store);
				return [...merged.keys()].sort(compareKeys).map((key) => ({ key, value: copy(merged.get(key)) }));
			}),
			clear: (store) => Promise.resolve().then(() => {
				check(store).clear();
				cleared.add(store);
			}),
		};
		const commit = () => {
			for(const store of cleared) {
				data.get(store).clear();
			}
			for(const [store, pending] of writes) {
				for(const [key, value] of pending) {
					if(value === DELETED) {
						data.get(store).delete(key);
					} else {
						data.get(store).set(key, value);
					}
				}
			}
		};
		return { tx, commit };
	};

	return {
		persistent: false,
		get closed() {
			return closed;
		},
		close() {
			closed = true;
		},
		run(storeNames, mode, work) {
			const names = [].concat(storeNames);
			const result = queue.then(() => {
				if(closed) {
					throw new Error('The local database is closed.');
				}
				const { tx, commit } = transactionOver(names);
				return Promise.resolve().then(() => work(tx)).then((value) => {
					if(mode === 'readwrite') {
						commit();
					}
					return value;
				});
			});
			queue = result.catch(() => {});
			return result;
		},
	};
}
